import type { ReactNode } from "react";
import { Menu } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ThemeToggle } from "@/components/theme-toggle";

export interface PanelTopbarProps {
  collapsed: boolean;
  mobileMenuOpen?: boolean;
  onToggleSidebar: () => void;
  onMobileMenuToggle: () => void;
  breadcrumbPrefix: string;
  pageTitle: string;
  actions?: ReactNode;
}

const PanelTopbar = ({
  collapsed,
  mobileMenuOpen = false,
  onToggleSidebar,
  onMobileMenuToggle,
  breadcrumbPrefix,
  pageTitle,
  actions,
}: PanelTopbarProps) => {
  const handleMenuClick = () => {
    if (window.innerWidth < 1024) {
      onMobileMenuToggle();
    } else {
      onToggleSidebar();
    }
  };

  const menuLabel =
    window.innerWidth < 1024
      ? mobileMenuOpen
        ? "Close menu"
        : "Open menu"
      : collapsed
        ? "Expand sidebar"
        : "Collapse sidebar";

  return (
    <header className="glass-bar sticky top-0 z-20 h-16 shrink-0">
      <div className="flex h-full items-center justify-between gap-2 pl-1 pr-2 sm:pl-2 sm:pr-3 lg:pl-3 lg:pr-5">
        <div className="flex min-w-0 items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            className="glass-chip h-10 w-10 shrink-0 rounded-xl p-0 text-foreground hover:bg-transparent hover:text-foreground focus:bg-transparent active:bg-transparent"
            onClick={handleMenuClick}
            aria-label={menuLabel}
            aria-expanded={window.innerWidth < 1024 ? mobileMenuOpen : undefined}
          >
            <Menu className="h-5 w-5 stroke-[2.25]" />
          </Button>

          <div className="hidden min-w-0 items-center gap-1.5 text-sm md:flex">
            <span className="text-xs text-muted-foreground">{breadcrumbPrefix}</span>
            <span className="text-xs text-muted-foreground/70">/</span>
            <span className="truncate text-xs font-semibold text-foreground sm:text-sm">
              {pageTitle}
            </span>
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-0.5 sm:gap-1.5">
          <ThemeToggle />
          {actions}
        </div>
      </div>
    </header>
  );
};

export default PanelTopbar;
